import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormRow } from '@gravity-ui/components';
import { DatePicker } from '@gravity-ui/date-components';
import { dateTimeParse, settings } from '@gravity-ui/date-utils';
import { ArrowRight, CircleQuestion, LogoTelegram } from '@gravity-ui/icons';
import { Button, Icon, Select, Sheet, TextArea, TextInput, Tooltip } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/query';
import { format } from 'date-fns';

import { useSafeParams } from 'hooks';
import { useAskConsentPatientMutation, useGetBlankQuery, useGetYearsQuery } from 'store/api';
import type { Blank } from 'store/types/common';

import { GenderType, getSpokesmanTypeText, SpokesmanType } from '../../utils';
import { REVERSE_GENDER_MAPPING, REVERSE_SPOKSMAN_MAPPING } from '../constants';
import additionalData from '../data.json';
import { Institution } from '../utils';

import { Loader } from './utils/load-skeleton';
import { useStepFormText } from './utils/use-step-form-text';
import type { StepOneValidationSchema } from './utils/use-validation';
import { getStepOneValidationSchema } from './utils/use-validation';
import { DatePickerForm } from './utils/date-picker';
import { YearSelect } from './utils/year-select';
import { GenderSelect } from './utils/gender-select';
import { QuestionsSelect } from './utils/questions-select';
import { ViolationsSelect } from './utils/violations-select';
import { FormTextInput, FormTextArea } from 'features/ui-kit';
import styles from './styles.module.scss';

interface StepOneFormProps {
	active: number;
	onStepComplete: (data: StepOneValidationSchema) => void;
	isMobile: any;
}

export const StepOneForm = ({ active, onStepComplete, isMobile }: StepOneFormProps) => {
	const [lang, setLang] = useState<boolean>(false);

	const { text: formText } = useStepFormText();

	const { clientId } = useSafeParams();
	const {
		data,
		isLoading: dataLoading,
		isSuccess: dataSuccess,
	} = useGetBlankQuery(clientId ?? skipToken, {
		refetchOnMountOrArgChange: true,
	});

	const { data: years } = useGetYearsQuery();

	const [askConsentPatient, { isLoading, isSuccess, isError }] = useAskConsentPatientMutation();

	const stepOneValidationSchema = useMemo(() => getStepOneValidationSchema(), []);

	useEffect(() => {
		if (data) {
			console.log('шаг 1', data);
		}
	}, [data]);

	const methods = useForm<StepOneValidationSchema>({
		resolver: zodResolver(stepOneValidationSchema),

		defaultValues: {
			createdBlankAt: '',
			firstName: '',
			middleName: '',
			lastName: '',
			birthday: '',
			gender: '',
			establishment: '',
			program: '',
			spokesman: '',
			diagnosis: '',
			additionalInformation: '',
			customerRequest: '',
			nextInterview: '',
			email: '',
			optionalEmail: undefined,
		},
		// mode: 'onBlur',
		mode: 'onChange',
	});

	const {
		handleSubmit,
		register,
		watch,
		setValue,
		control,
		formState: { errors, isSubmitted, isValid },
	} = methods;

	const formValues = watch();

	useEffect(() => {
		settings.loadLocale('ru').then(() => {
			settings.setLocale('ru');
			setLang(true);
		});
	}, []);

	//TODO - переделать ниже - по идее убрать полностью
	useEffect(() => {
		if (data) {
			(Object.keys(data) as Array<keyof Blank>).forEach((key) => {
				const value = data[key];

				if (key !== 'problems' && key !== 'patientEstimates') {
					if (key === 'establishment' && value && typeof value === 'object' && 'name' in value) {
						setValue(key, (value as { name: string }).name);
					} else if (key === 'gender') {
						if (typeof value === 'string') {
							const genderArray = REVERSE_GENDER_MAPPING[value] ?? value;
							setValue(key, genderArray);
						}
					} else if (key === 'spokesman') {
						if (typeof value === 'string') {
							// Используем обратное маппирование для spokesman (из серверного формата в UI формат)
							const spokesmanArray = REVERSE_SPOKSMAN_MAPPING[value] ?? value;
							setValue(key, spokesmanArray);
						}
					} else if (key === 'optionalEmail') {
						if (value && typeof value === 'string' && value.trim() !== '') {
							setValue(key, value);
						} else {
							setValue(key, undefined);
						}
					} else {
						if (typeof value === 'string' || typeof value === 'number' || value === null) {
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore TODO проблема с consent
							setValue(key, value);
						}
					}
				}
			});
		}

		if (!data?.createdBlankAt) {
			const today = format(new Date(), 'yyyy-MM-dd');
			setValue('createdBlankAt', today, { shouldValidate: true });
		}
	}, [active, data, setValue]);

	const onSubmit = useCallback(
		(data: StepOneValidationSchema) => {
			onStepComplete(data);
		},
		[onStepComplete],
	);

	const emailSending = () => {
		const { email, firstName } = formValues;
		const id = data?.id ?? 0;

		askConsentPatient({ email, name: firstName, id })
			.unwrap()
			.then(() => {
				toast('Письмо отправлено');
				console.log('Письмо отправлено');
			})
			.catch((error) => {
				console.error('Ошибка при отправке письма:', error);
			});
	};

	const genderOptions = useMemo(
		() => [
			{ value: GenderType.MALE, content: 'М' },
			{ value: GenderType.FEMALE, content: 'Ж' },
		],
		[],
	);

	const yearsOptions = useMemo(
		() =>
			years?.map((code) => ({
				value: code.toString(),
				content: code,
			})) ?? [],
		[years],
	);

	const violationsOptions = useMemo(
		() =>
			additionalData.violations.map((violation) => ({
				value: violation,
				content: violation,
			})),
		[],
	);

	const spokesmanOptions = useMemo(
		() => [
			{ value: SpokesmanType.CLIENT_ADULT, content: getSpokesmanTypeText(SpokesmanType.CLIENT_ADULT) },
			{ value: SpokesmanType.CLIENT_CHILD, content: getSpokesmanTypeText(SpokesmanType.CLIENT_CHILD) },
			{ value: SpokesmanType.PARENT, content: getSpokesmanTypeText(SpokesmanType.PARENT) },
			{
				value: SpokesmanType.CLOSE_CARING_ADULT,
				content: getSpokesmanTypeText(SpokesmanType.CLOSE_CARING_ADULT),
			},
		],
		[],
	);

	return (
		<>
			{dataLoading && <Loader />}

			{!dataLoading && (
				<FormProvider {...methods}>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className={styles.info}>
							<h3 className={styles.info__title}>{formText.infoTitle}</h3>

							<DatePickerForm
								isMobile={isMobile}
								formText={formText}
								lang={lang}
								register={register}
								formValues={formValues}
								setValue={setValue}
								isSubmitted={isSubmitted}
								errors={errors}
							/>

							<FormTextInput
								className={styles.info__row}
								name="lastName"
								label={formText.familyNameBlank}
								placeholder={formText.familyName}
								required
							/>

							<FormTextInput
								className={styles.info__row}
								name="firstName"
								label={formText.nameBlank}
								placeholder={formText.name}
								required
							/>

							<FormTextInput
								className={styles.info__row}
								name="middleName"
								label={formText.surnameBlank}
								placeholder={formText.surname}
							/>

							<div className={styles.info__row_group}>
								<YearSelect
									isMobile={isMobile}
									formText={formText}
									yearsOptions={yearsOptions}
									register={register}
									formValues={formValues}
									setValue={setValue}
									isSubmitted={isSubmitted}
									errors={errors}
								/>

								<GenderSelect
									isMobile={isMobile}
									formText={formText}
									genderOptions={genderOptions}
									register={register}
									formValues={formValues}
									setValue={setValue}
									isSubmitted={isSubmitted}
									errors={errors}
									control={control}
								/>
							</div>

							<FormRow className={styles.info__row} label={formText.institutionBlank} required>
								<Institution
									hasClear
									{...register('establishment', { required: true })}
									validationState={isSubmitted && errors.establishment ? 'invalid' : undefined}
									errorMessage={
										isSubmitted && errors.establishment ? 'Обязательно для заполнения' : undefined
									}
									onItemSelect={(item) => {
										if (item) {
											setValue('establishment', item.value);
										}
									}}
									onValueChange={(value) => {
										setValue('establishment', value);
									}}
									value={watch('establishment')}
									errorPlacement="inside"
								/>
							</FormRow>

							<FormTextInput
								className={styles.info__row}
								name="program"
								label={formText.programBlank}
								placeholder={formText.program}
							/>

							<QuestionsSelect
								isMobile={isMobile}
								formText={formText}
								spokesmanOptions={spokesmanOptions}
								register={register}
								formValues={formValues}
								setValue={setValue}
								isSubmitted={isSubmitted}
								errors={errors}
								control={control}
							/>
							<ViolationsSelect
								isMobile={isMobile}
								formText={formText}
								violationsOptions={violationsOptions}
								register={register}
								formValues={formValues}
								setValue={setValue}
								isSubmitted={isSubmitted}
								errors={errors}
								control={control}
							/>

							<FormTextArea
								className={`${styles.info__row} ${styles.top}`}
								name="diagnosis"
								label={formText.diagnosisBlank}
								placeholder={formText.diagnosis}
							/>

							<FormTextArea
								className={`${styles.info__row} ${styles.top}`}
								name="additionalInformation"
								label={formText.additionalInfoBlank}
								placeholder={formText.additionalInfo}
							/>

							<FormTextArea
								className={`${styles.info__row} ${styles.top}`}
								name="customerRequest"
								label={formText.clientRequestBlank}
								placeholder={formText.clientRequest}
								required
							/>

							<FormRow className={styles.info__row} label={formText.interviewDateBlank}>
								{lang && (
									<Controller
										control={control}
										name="nextInterview"
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<DatePicker
												size="xl"
												hasClear
												timeZone="Europe/Moscow"
												value={value ? dateTimeParse(value) : undefined}
												onUpdate={(dateValue) => {
													if (!dateValue) {
														onChange(undefined);
														return;
													}
													const jsDate = dateValue.toDate();
													const formatted = format(jsDate, 'yyyy-MM-dd');
													onChange(formatted);
												}}
												validationState={isSubmitted && error ? 'invalid' : undefined}
												errorPlacement="inside"
												errorMessage={
													isSubmitted && error ? 'Обязательно для заполнения' : undefined
												}
											/>
										)}
									/>
								)}
							</FormRow>

							<div className={styles.info__agreement}>
								<h3>{formText.agreementBlank}</h3>
								<Tooltip closeDelay={2000} content={<span>{formText.tooltip}</span>}>
									<div tabIndex={0} className={styles.profile__inputs__group__label__tooltip__icon}>
										<CircleQuestion />
									</div>
								</Tooltip>
							</div>
							<FormRow
								className={`${styles.info__row} ${styles.email}`}
								label={formText.emailBlank}
								required
							>
								<Controller
									control={control}
									name="email"
									render={({ field: { onChange, onBlur, ref, value }, fieldState: { error } }) => (
										<TextInput
											placeholder={formText.email}
											size="xl"
											hasClear
											error={error?.message}
											value={value || ''}
											onChange={onChange}
											onBlur={onBlur}
											ref={ref}
											errorPlacement="inside"
										/>
									)}
								/>

								<Button
									disabled={!!errors.email || !formValues.email}
									view={formValues.email && !errors.email ? 'outlined' : 'raised'}
									size="xl"
									onClick={emailSending}
								>
									<Icon data={LogoTelegram} size={18} />
									{formText.btnSend}
								</Button>
							</FormRow>

							<FormTextInput
								className={styles.info__row}
								name="optionalEmail"
								label="Дополнительный Email"
								placeholder="Введите дополнительный email (необязательно)"
								required={false}
							/>

							<div className={styles.info__btn_container}>
								{active !== 3 && (
									<Button
										size="xl"
										type="submit"
										// disabled={!isValid}
										// view={isValid ? 'action' : 'raised'}
									>
										{formText.save} <Icon data={ArrowRight} size={18} />
									</Button>
								)}
							</div>
						</div>
					</form>
				</FormProvider>
			)}
		</>
	);
};
